import {createContext, ReactNode, useContext, useMemo, useState} from 'react'

type KpiPanelContextValue = {
  /** Whether the KPI panel for `nodeId` is currently open. */
  isOpen: (nodeId: string) => boolean
  /** Toggles the KPI panel for `nodeId`, closing any other panel that was open. */
  toggle: (nodeId: string) => void
}

const KpiPanelContext = createContext<KpiPanelContextValue | null>(null)

/**
 * The DOM id of a node's KPI panel, shared by the toggle button (`aria-controls`) and the
 * panel itself (`id`) so assistive tech can tie the two together.
 */
export const kpiPanelId = (nodeId: string): string => `pamm-kpi-panel-${nodeId}`

/**
 * The DOM id of a node's offer card, used as the scroll target when an Agent Chain breadcrumb pill
 * for an upstream node is clicked (PT-5713).
 */
export const nodeAnchorId = (nodeId: string): string => `pamm-node-${nodeId}`

type KpiPanelProviderProps = {
  children: ReactNode
}

/**
 * Holds which node's KPI panel is open, shared across the whole tree so the
 * "only one panel open at a time per view" rule (PT-5710) is enforced in one place:
 * opening a panel simply overwrites the single open id, which closes the previous one.
 *
 * The single-open policy was chosen over the multi-open (cap-at-3) alternative the ticket
 * offered — one panel keeps the tree compact and the "money" view unambiguous, and avoids
 * the auto-close-oldest bookkeeping that multi-open would need.
 *
 * @param children - The tree subtree that renders the offer cards and their KPI panels.
 */
export const KpiPanelProvider = ({children}: KpiPanelProviderProps) => {
  const [openId, setOpenId] = useState<string | null>(null)

  const value = useMemo<KpiPanelContextValue>(
    () => ({
      isOpen: (nodeId) => openId === nodeId,
      // Re-clicking the open node closes it; any other node replaces the open id.
      toggle: (nodeId) => setOpenId((current) => (current === nodeId ? null : nodeId)),
    }),
    [openId],
  )

  return <KpiPanelContext.Provider value={value}>{children}</KpiPanelContext.Provider>
}

/**
 * Reads the shared KPI-panel open state. Must be called from within a {@link KpiPanelProvider}
 * (always the case inside the rendered tree); throwing otherwise surfaces a wiring mistake early
 * instead of silently no-op'ing the toggle.
 */
export const useKpiPanel = (): KpiPanelContextValue => {
  const context = useContext(KpiPanelContext)
  if (!context) throw new Error('useKpiPanel must be used within a KpiPanelProvider')
  return context
}
